import type { en } from './en';

/**
 * The Nepali strings.
 *
 * A PARTIAL of `en` on purpose, and that is the whole working method: a key
 * that is not here falls back to English rather than to a gap, so English copy
 * can be added today and translated whenever the translation arrives, without
 * a release ever showing somebody a blank space.
 *
 * Which means a translator can work in this file alone. Add a key, it appears
 * in Nepali. Remove one, it goes back to English. Nothing else has to change
 * and no code has to be touched.
 *
 * WHAT IS DELIBERATELY ABSENT: the landing page's marketing prose. That lives
 * in `site.ts` and is the institute's own voice — it needs a Nepali writer
 * rather than a developer with a dictionary, and it is tracked separately.
 *
 * A note for whoever reviews this: these are the functional strings — buttons,
 * labels, table headings. They are meant to be plain and unambiguous rather
 * than elegant. Where a term is more commonly used in English by Nepali
 * speakers ("SMS", "WhatsApp", "Email"), it is left in English on purpose,
 * because translating it would be less clear, not more.
 */
/*
 * NUMERALS ARE LATIN, not Devanagari, and consistently so. Every number the
 * app interpolates — a score, a countdown, a question number — arrives as
 * 0-9, so a hardcoded १ beside a rendered 1 puts two number systems in one
 * sentence. Whether the product should use Devanagari numerals everywhere is
 * a decision for the client; until it is made, one system is better than two.
 */
export const ne: Partial<Record<keyof typeof en, string>> = {
  // ----------------------------------------------------------------- common
  'common.save': 'परिवर्तन सुरक्षित गर्नुहोस्',
  'common.saving': 'सुरक्षित गर्दै…',
  'common.cancel': 'रद्द गर्नुहोस्',
  'common.confirm': 'पक्का गर्नुहोस्',
  'common.error_title': 'केही गडबड भयो',
  'common.error_body': 'पृष्ठको यो भाग देखाउन सकिएन। फेरि प्रयास गर्न पृष्ठ रिलोड गर्नुहोस् — तपाईंले पहिले सुरक्षित गरेको केही पनि हराउँदैन।',
  'common.error_load': 'यो लोड गर्दा केही गडबड भयो। कृपया फेरि प्रयास गर्नुहोस्।',
  'common.reload': 'पृष्ठ रिलोड गर्नुहोस्',
  'common.edit': 'सम्पादन',
  'common.loading': 'लोड हुँदै…',
  'common.back': 'पछाडि',

  // वि.सं. मा छानिने मिति (BsDateInput).
  'bsdate.day': 'गते',
  'bsdate.month': 'महिना',
  'bsdate.year': 'साल (वि.सं.)',
  'bsdate.time': 'समय',
  'bsdate.ad': '{date} ई.सं.',
  'bsdate.incomplete': 'गते, महिना र साल छान्नुहोस्।',
  'bsdate.before_min': '{date} वा त्यसपछिको मिति छान्नुहोस्।',
  'bsdate.after_max': '{date} वा त्योभन्दा अघिको मिति छान्नुहोस्।',

  // -------------------------------------------------------------- navigation
  'nav.menu': 'मेनु',
  'nav.you': 'तपाईं',
  'nav.dashboard': 'ड्यासबोर्ड',
  'nav.my_tests': 'मेरा परीक्षा',
  'nav.my_results': 'मेरा नतिजा',
  'nav.my_plan': 'मेरो योजना',
  'nav.billing': 'भुक्तानी',
  'nav.my_profile': 'मेरो प्रोफाइल',
  'nav.help': 'सहयोग',
  'nav.collapse': 'मेनु खुम्च्याउनुहोस्',
  'nav.expand': 'मेनु खोल्नुहोस्',
  'nav.open_menu': 'मेनु खोल्नुहोस्',
  'nav.close_menu': 'मेनु बन्द गर्नुहोस्',
  'nav.tests': 'परीक्षा',
  'nav.results': 'नतिजा',
  'nav.bill_due': 'अहिले तिर्नुपर्ने',
  'nav.bill_plans': 'योजना',
  'nav.bill_invoices': 'बिल',
  'nav.bill_activity': 'गतिविधि',
  'footer.label': 'फुटर',
  'footer.rights': '© {year} करिअर एकेडेमी। सर्वाधिकार सुरक्षित।',

  // ------------------------------------------------------------------ header
  'header.tagline': 'साप्ताहिक मूल्याङ्कन प्लेटफर्म',
  'header.sign_out': 'साइन आउट',
  'header.admin_console': 'प्रशासन कन्सोल',
  'header.mobile_unverified': 'मोबाइल प्रमाणित छैन',
  'header.language': 'भाषा',

  // ----------------------------------------------------------------- roles
  'role.parent': 'अभिभावक',
  'role.student': 'विद्यार्थी',
  'role.owner': 'सञ्चालक',
  'role.finance': 'लेखा',
  'role.academic': 'शैक्षिक',
  'role.reviewer': 'समीक्षक',
  'role.support': 'सहयोग',
  'role.signed_in': 'साइन इन',

  // ---------------------------------------------------------- notifications
  'notif.title': 'सूचनाहरू',
  'notif.mark_all_read': 'सबै पढिएको जनाउनुहोस्',
  'notif.empty': 'अहिलेसम्म केही छैन। सम्झना र नतिजा यहाँ देखिनेछ।',
  'notif.see_all': 'सबै सूचना हेर्नुहोस्',
  'notif.page_title': 'सूचनाहरू',
  'notif.unread_count': '{n} वटा अझै पढ्न बाँकी छ।',
  'notif.all_read': 'यहाँका सबै पढिसकिएका छन्।',
  'notif.everything_sent': 'हामीले पठाएका सबै',
  'notif.page_empty': 'अहिलेसम्म केही छैन। सम्झना, नतिजा र रसिद यहाँ राखिनेछ।',
  'notif.open': 'खोल्नुहोस्',
  'notif.to_open': 'खोल्नुहोस्',
  'notif.to_scorecard': 'अंकपत्र',
  'notif.to_invoices': 'बिलहरू',
  'notif.to_billing': 'भुक्तानी',
  'notif.to_account': 'तपाईंका उपकरणहरू',
  'notif.to_tests': 'परीक्षाहरू',
  'notif.to_support': 'सहयोग',
  'notif.to_dashboard': 'ड्यासबोर्ड',
  'notif.more': 'थप',
  'notif.less': 'कम',
  'notif.choose_channels':
    'यीमध्ये कुन Email, SMS वा WhatsApp बाट पाउने भन्ने आफ्नो खातामा छान्न सकिन्छ।',

  'when.just_now': 'भर्खरै',
  'when.minutes': '{n} मिनेट अघि',
  'when.hours': '{n} घण्टा अघि',
  'when.yesterday': 'हिजो',
  'when.days': '{n} दिन अघि',

  // --------------------------------------------------------------- account
  // ------------------------------------------- a student's own plan (074)
  'sub.card_for': "{name} को योजना।",
  'sub.card_shared': "{name} को योजना, {others} सँग साझा। यसमा भएका प्रत्येक विद्यार्थीको शुल्क लाग्छ।",
  'sub.card_family': "तपाईंका योजना, र प्रत्येकले के समेट्छ।",
  'sub.per_child': "{n} विद्यार्थी × {each}",
  'splan.title': 'मेरो योजना',
  'splan.lede': 'यहाँबाट आफ्नो योजना छानेर भुक्तानी गर्नुहोस्। यो तपाईंको परिवारको खातामा जान्छ, त्यसैले {parent} ले देख्नुहुन्छ र रसिद पाउनुहुन्छ।',
  'splan.ask_title': 'अभिभावकलाई भन्नुहोस्',
  'splan.too_young': 'कक्षा {n} देखि विद्यार्थीले आफ्नो योजना आफैं छानेर तिर्न सक्छन्। {parent} लाई उहाँको खाताबाट गर्न भन्नुहोस्।',
  'splan.choose_title': 'योजना छान्नुहोस्',
  'splan.choose_sub': 'छानेपछि भुक्तानी गर्न सक्नुहुन्छ। मूल्यमा भ्याट समावेश छ।',
  'splan.cycle_monthly': 'मासिक',
  'splan.cycle_annual': 'वार्षिक',
  'splan.no_plans': 'अहिले तपाईंको कक्षाका लागि कुनै योजना उपलब्ध छैन। कार्यालयमा सोध्नुहोस्।',
  'splan.choose_btn': 'यो योजना छान्नुहोस्',
  'splan.yours_title': 'तपाईंको योजना',
  'splan.incl_registration': 'एक पटकको {amount} दर्ता शुल्क समावेश',
  'splan.parent_mobile': 'भुक्तानी गर्नुअघि {parent} ले आफ्नो मोबाइल नम्बर प्रमाणित गर्नुपर्छ — नवीकरण सूचना त्यहीँ पठाइन्छ।',
  'splan.family_note': 'बिल र रसिद {parent} लाई जान्छ, जसले यो योजना हेर्न र परिवर्तन गर्न पनि सक्नुहुन्छ।',
  'splan.pending': 'भुक्तानी अझै बाँकी देखिन्छ। अहिलेसम्म केही कटाइएको छैन — केही मिनेटपछि फेरि हेर्नुहोस्।',
  'splan.pay_link': 'योजना छानेर भुक्तानी गर्नुहोस्',
  'splan.paid_up': '{date} सम्म भुक्तानी भइसकेको छ। अर्को महिनाको भुक्तानी अन्तिम हप्तामा गर्न सकिन्छ।',

  // ---------------------------------------- landing announcement (pop-up)
  'pop.close': 'घोषणा बन्द गर्नुहोस्',
  'pop.tagline': 'आज मनाउँदै, भोलि प्रेरित गर्दै',
  'pop.anniv_number': '२५',
  'pop.anniv_suffix': 'औँ',
  'pop.anniv_title': 'वार्षिकोत्सव',
  'pop.anniv_of': 'CAREER ACADEMY को',
  'pop.trust': 'विश्वास, उत्कृष्टता र शिक्षाप्रतिको प्रतिबद्धताका २५ वर्ष',
  'pop.logo_alt': 'CST नेपाल — क्यारियर छात्रवृत्ति परीक्षा',
  'pop.banner_title': 'क्यारियर छात्रवृत्ति परीक्षा (CST) | कक्षा १ – १२',
  'pop.banner_sub': 'आफ्नो प्रतिभा परीक्षण गर्नुहोस्, छात्रवृत्ति जित्नुहोस्, भविष्य निर्माण गर्नुहोस्।',
  'pop.why_title': 'CST मा किन सहभागी हुने ?',
  'pop.b1_label': 'छात्रवृत्ति:',
  'pop.b1': 'आकर्षक छात्रवृत्ति र पुरस्कार जित्नुहोस्',
  'pop.b2_label': 'क्षमताको मूल्याङ्कन:',
  'pop.b2': 'आफ्ना शैक्षिक सबल पक्षहरूको मूल्याङ्कन गर्नुहोस्',
  'pop.b3_label': 'राष्ट्रिय मान्यता:',
  'pop.b3': 'नेपालभरका विद्यार्थीहरूसँग प्रतिस्पर्धा गर्नुहोस्',
  'pop.b4_label': 'प्रमाणपत्र र पुरस्कार:',
  'pop.b4': 'उत्कृष्ट प्रदर्शन गर्नेलाई योग्यता प्रमाणपत्र',
  'pop.details_title': 'परीक्षाको विवरण',
  'pop.when': 'कहिले',
  'pop.when_v': 'प्रत्येक शनिबार',
  'pop.time': 'परीक्षा समय',
  'pop.time_v': 'बिहान ८:०० – १०:००',
  'pop.results': 'नतिजा',
  'pop.results_v': 'बिहान ११:०० बजेदेखि घोषणा',
  'pop.format': 'ढाँचा',
  'pop.format_v': 'साप्ताहिक परीक्षा | ५० पूर्णाङ्क | ५० मिनेट',
  'pop.get': 'तपाईंले पाउनुहुने',
  'pop.get_v': 'श्रेणीक्रम, विश्लेषण र प्रदर्शन प्रतिवेदन',
  'pop.price_title': 'सस्तो र सुलभ',
  'pop.reg_label': 'प्रारम्भिक दर्ता शुल्क',
  'pop.reg_amount': 'रु. १००',
  'pop.monthly_label': 'मासिक भुक्तानी (४ हप्ता)',
  'pop.monthly_amount': 'रु. ५००',
  'pop.bonus': 'मासिक प्याकेज बोनस : २०० मध्ये १६० अङ्क ल्याउनुहोस् र अर्को महिनाको छात्रवृत्ति जित्नुहोस् !',
  'pop.teachers_title': 'अनुभवी शिक्षकहरूद्वारा निर्मित',
  'pop.teachers': 'CST सेन्ट जेभियर्सका पूर्व शिक्षक श्री RMJHA, सेन्ट मेरिज, पाठशाला र अन्य २२ प्रतिष्ठित विद्यालयका शिक्षकहरूको टोलीद्वारा निर्माण गरिँदै छ।',
  'pop.register_title': 'अहिल्यै दर्ता गर्नुहोस्',
  'pop.phone': 'फोन',
  'pop.email': 'इमेल',
  'pop.address': 'Career भवन, जावलाखेल, ललितपुर',

  'account.title': 'खाता',
  'account.lede': 'तपाईंको विवरण, पासवर्ड, र अहिले साइन इन भएका सबै उपकरण।',
  'account.profile_title': 'मेरो प्रोफाइल',
  'account.profile_sub': 'तपाईंको नाम र हामी सम्पर्क गर्ने माध्यम।',
  'account.name': 'नाम',
  'account.email': 'इमेल',
  'account.mobile': 'मोबाइल',
  'account.address_legend': 'तपाईंको परिवार बस्ने ठेगाना',
  'account.province': 'प्रदेश',
  'account.permanent_address': 'स्थायी ठेगाना',
  'account.local_address': 'अस्थायी ठेगाना',
  'account.not_set': 'राखिएको छैन',
  'account.unverified': 'प्रमाणित छैन',
  'account.saved': 'तपाईंको विवरण सुरक्षित भयो।',
  'account.verify_again': 'मोबाइल परिवर्तन गरेपछि पुनः प्रमाणित गर्नुपर्छ।',
  'account.email_change': 'इमेल परिवर्तन',
  'account.email_change_title': 'तपाईंको इमेल परिवर्तन गर्नुहोस्',
  'account.email_change_lede': 'हामी नयाँ ठेगानामा कोड पठाउँछौं। कोड हालेपछि मात्र इमेल परिवर्तन हुन्छ — त्यतिन्जेल पुरानै इमेलबाट साइन इन हुन्छ।',
  'account.new_email': 'नयाँ इमेल ठेगाना',
  'account.current_password': 'हालको पासवर्ड',
  'account.current_password_hint': 'यो परिवर्तन तपाईंले नै गर्दै हुनुहुन्छ भनेर थाहा पाउन।',
  'account.send_code': 'कोड पठाउनुहोस्',
  'account.sending': 'पठाउँदै…',
  'account.code_sent': '{email} मा ६ अङ्कको कोड पठाइयो। यो ३० मिनेटमा समाप्त हुन्छ।',
  'account.code': 'कोड',
  'account.confirm_change': 'परिवर्तन पक्का गर्नुहोस्',
  'account.confirming': 'पक्का गर्दै…',
  'account.cancel_change': 'परिवर्तन रद्द गर्नुहोस्',
  'account.email_changed': 'तपाईंको इमेल अब {email} हो। पुरानो ठेगानालाई पनि जानकारी दिइयो।',
  'account.pending_email': '{email} मा पठाइएको कोड पर्खँदै।',
  'account.enter_code': 'कोड हाल्नुहोस्',
  'account.email_readonly': "इमेल परिवर्तन गर्न \"इमेल परिवर्तन\" प्रयोग गर्नुहोस्।",
  'account.mobile_password': 'मोबाइल परिवर्तन गर्न हालको पासवर्ड चाहिन्छ।',
  'account.details_title': 'दर्ता',
  'account.details_sub': 'तपाईंको नम्बर र पढ्ने ठाउँ। केही गलत भए कार्यालयलाई भन्नुहोस्।',
  'account.details_sub_family': 'तपाईंको परिवार नम्बर, जसबाट कार्यालयले खाता भेट्टाउँछ। केही गलत भए कार्यालयलाई भन्नुहोस्।',
  'account.reg_no': 'दर्ता नं.',
  'account.family_no': 'परिवार नं.',
  'account.class': 'कक्षा',
  'account.group': 'समूह',
  'account.section': 'सेक्सन',
  'account.school': 'विद्यालय',
  'account.parent': 'अभिभावक',
  'account.family_title': 'तपाईंका छोराछोरी',
  'account.no_children': 'यो खातामा अहिलेसम्म कुनै विद्यार्थी छैन।',
  'account.show_all': 'सबै {n} देखाउनुहोस्',
  'account.show_fewer': 'कम देखाउनुहोस्',
  'account.subscription_title': 'सदस्यता',
  'account.no_subscription': 'अहिलेसम्म सदस्यता छैन।',
  'account.renews': '{date} सम्म',
  'account.manage_billing': 'बिल र रसिद',
  'account.activity_title': 'खाता गतिविधि',
  'account.member_since': 'सदस्य भएको मिति',
  'account.last_sign_in': 'अन्तिम साइन इन',
  'account.sign_in_with': 'साइन इन माध्यम',
  'account.method_password': 'इमेल र पासवर्ड',
  'account.method_google': 'गुगल',
  'account.never': 'कहिल्यै होइन',
  'sub.st_trial': 'नि:शुल्क परीक्षण',
  'sub.st_active': 'भुक्तानी भएको',
  'sub.st_grace': 'नवीकरण बाँकी',
  'sub.st_past_due': 'भुक्तानी ढिला',
  'sub.st_suspended': 'निलम्बित',
  'sub.st_paused': 'रोकिएको',
  'sub.st_cancelled': 'रद्द',
  'sub.st_expired': 'समाप्त',

  // --------------------------------------------------- notification channels
  'channels.title': 'हामी तपाईंलाई कसरी सम्पर्क गर्छौं',
  'channels.sub': 'कुनै एक बन्द गरे पनि अरूबाट खबर पुग्छ।',
  'channels.email': 'Email',
  'channels.email_blurb': 'रसिद, नतिजा र नवीकरणको सम्झना। सधैं भरपर्दो माध्यम।',
  'channels.sms': 'SMS',
  'channels.sms_blurb':
    'प्रमाणित मोबाइलमा छोटो सम्झना। अप्रमाणित नम्बरमा केही पठाइँदैन।',
  'channels.whatsapp': 'WhatsApp',
  'channels.whatsapp_blurb':
    'SMS कुर्नुको साटो उही सम्झना WhatsApp मा। तपाईंले नमागेसम्म बन्द रहन्छ।',
  'channels.in_app': 'एपभित्र',
  'channels.in_app_blurb':
    'सूचना घण्टीमा राखिन्छ, त्यसैले छुटेको खबर पछि पनि भेटिन्छ।',
  'channels.all_off_warning':
    'Email, SMS र WhatsApp तीनै बन्द भए एप बाहिर तपाईंसम्म केही पुग्दैन — '
    + 'सदस्यता सकिन लागेको सम्झना पनि।',

  // --------------------------------------------------------------- student
  'student.hello': 'नमस्ते, {name}',
  'student.next_test': 'तपाईंको अर्को परीक्षा',
  'student.no_test': 'तपाईंको कक्षाको परीक्षा अझै तय भएको छैन। केही समयपछि हेर्नुहोस्।',
  'student.until_opens': 'खुल्न बाँकी',
  'student.start_test': 'परीक्षा सुरु गर्नुहोस्',
  'student.test_open': 'तपाईंको परीक्षा अहिले खुला छ।',
  'student.finished': 'तपाईंले यो परीक्षा सक्नुभयो। सबैले सकेपछि नतिजा आउँछ।',
  'student.one_at_a_time':
    'यो परीक्षामा एक पटकमा एउटा प्रश्न देखिन्छ र पछाडि फर्कन मिल्दैन — '
    + 'त्यसैले अघि बढ्नुअघि प्रत्येकको उत्तर दिनुहोस्।',
  'student.last_result': 'तपाईंको पछिल्लो नतिजा',
  'student.see_what_right': 'के-के ठीक भयो हेर्नुहोस्',
  'student.test_history': 'परीक्षाको इतिहास',
  'student.tests_taken': 'दिएका परीक्षा',
  'student.results_out': 'आएका नतिजा',
  'student.every_test': 'तपाईंले दिनुभएका सबै परीक्षा',
  'student.back_to_account': 'मेरो खातामा फर्कनुहोस्',
  'student.child_last_result': '{name} — पछिल्लो नतिजा',
  'student.full_scorecard': 'पूरा नतिजा हेर्नुहोस्',
  'when.today': 'आज',
  'when.tomorrow': 'भोलि',
  'when.in_days': '{n} दिनमा',

  // -------------------------------------------------------------- scorecard
  'score.score': 'अंक',
  'score.class_rank': 'कक्षामा स्थान',
  'score.band': 'स्तर',
  'score.time_used': 'लागेको समय',
  'score.vs_last': 'गत पटकको तुलनामा',
  'score.accuracy': 'शुद्धता',
  'score.by_subject': 'विषयअनुसार',
  'score.by_subject_alt': 'यो परीक्षामा विषयअनुसार शुद्धता, सोधिएका प्रश्नको प्रतिशतमा: {summary}।',
  'score.correct': '{n} ठीक',
  'score.missed': '{n} छुटे',
  'score.lowest_here': 'सबैभन्दा कमजोर',
  'score.correct_on_paper': 'यस परीक्षामा {total} मध्ये {correct} ठीक',
  'score.improved': '{who} गत परीक्षादेखि {from}% बाट {to}% पुग्नुभयो',
  'score.next_paper': 'अर्को परीक्षा',
  'score.minutes': 'मिनेट',

  // ------------------------------------------------------------------- auth
  'auth.sign_in': 'साइन इन',
  'auth.create_account': 'खाता बनाउनुहोस्',
  'auth.welcome_back': 'फेरि स्वागत छ! आफ्नो खातामा साइन इन गर्नुहोस्।',
  'auth.signing_in': 'साइन इन हुँदै…',
  'auth.password_show': 'पासवर्ड देखाउनुहोस्',
  'auth.password_hide': 'पासवर्ड लुकाउनुहोस्',
  'auth.forgot_link': 'पासवर्ड बिर्सनुभयो?',
  'auth.back_to_sign_in': 'साइन इनमा फर्कनुहोस्',
  'auth.confirm_password': 'पासवर्ड पुनः लेख्नुहोस्',
  'auth.password_rules': 'कम्तीमा १० अक्षर, अक्षर र अङ्क दुवै सहित।',
  'auth.passwords_differ': 'दुई पासवर्ड मिलेनन्।',

  // ---------------------------------------------------------- password reset
  'forgot.title': 'पासवर्ड रिसेट गर्नुहोस्',
  'forgot.intro': 'साइन इन गर्ने इमेल लेख्नुहोस्। त्यसको खाता भए रिसेट कोड इमेलमा '
    + 'पठाइनेछ।',
  'forgot.send_code': 'कोड पठाउनुहोस्',
  'forgot.sending': 'पठाउँदै…',
  'forgot.sent': '{email} को खाता भए कोड इमेल गरिएको छ। यो ३० मिनेटमा समाप्त हुन्छ।',
  'forgot.code': 'इमेलमा आएको रिसेट कोड',
  'forgot.new_password': 'नयाँ पासवर्ड',
  'forgot.reset': 'नयाँ पासवर्ड राख्नुहोस्',
  'forgot.resetting': 'सुरक्षित गर्दै…',
  'forgot.resend': 'नयाँ कोड पठाउनुहोस्',
  'forgot.done': 'पासवर्ड परिवर्तन भयो, र अन्य सबै डिभाइसबाट साइन आउट गरियो। नयाँ '
    + 'पासवर्डले साइन इन गर्नुहोस्।',

  // -------------------------------------------------------- staff invitation
  'invite.title': 'Career Academy मा सामेल हुनुहोस्',
  'invite.intro': 'तपाईंलाई स्टाफ कन्सोलमा निम्तो गरिएको छ। सहकर्मीले देख्ने नाम र '
    + 'तपाईंलाई मात्र थाहा हुने पासवर्ड छान्नुहोस्।',
  'invite.code': 'निमन्त्रणा कोड',
  'invite.code_hint': 'निमन्त्रणा इमेलबाट। लिङ्क खोल्दा यो आफैँ भरिन्छ।',
  'invite.full_name': 'पूरा नाम',
  'invite.password': 'पासवर्ड',
  'invite.accept': 'निमन्त्रणा स्वीकार गर्नुहोस्',
  'invite.accepting': 'खाता बनाउँदै…',

  // ----------------------------------------------------------- parent cards
  'parent.verify_mobile': 'आफ्नो मोबाइल प्रमाणित गर्नुहोस्',
  'parent.send_code': 'कोड पठाउनुहोस्',
  'parent.tests': 'परीक्षाहरू',
  'stat.amount_due': 'तिर्न बाँकी',
  'stat.nothing_due': 'केही बाँकी छैन',
  'stat.go_billing': 'भुक्तानीमा जानुहोस्',
  'stat.last_score': 'पछिल्लो अंक',
  'stat.view_scorecard': 'नतिजा पत्र हेर्नुहोस्',
  'stat.no_result': 'अझै नतिजा छैन',
  'stat.locked_no_plan': 'यो बच्चालाई अहिले कुनै योजनाले समेटेको छैन ।',
  'stat.locked_pay': 'भुक्तानी पुष्टि भएपछि खुल्नेछ',
  'stat.locked_state': 'यो सदस्यता {state} छ',
  'stat.view_details': 'विवरण हेर्नुहोस्',

  'dash.morning': 'शुभ प्रभात,',
  'dash.afternoon': 'शुभ दिन,',
  'dash.evening': 'शुभ सन्ध्या,',
  'dash.note': 'आजको अभ्यास, भोलिको उज्यालो।',
  'dash.note_sub': 'लागिरहनुहोस्!',

  'perf.title': 'विषयगत प्रदर्शन',
  'perf.sub': 'प्रकाशित सबै परीक्षाको शुद्धता, कमजोर पहिले।',
  'perf.none': 'अझै देखाउने केही छैन — नतिजा प्रकाशित भएपछि यो भरिन्छ।',
  'perf.full_report': 'पूरा प्रतिवेदन हेर्नुहोस्',
  'perf.of_questions': '{asked} मध्ये {correct} प्रश्न सही',

  'po.title': 'प्रदर्शनको सारांश',
  'po.accuracy_label': 'यो परीक्षाको शुद्धता',
  'po.last_test': 'पछिल्लो परीक्षा',

  'up.title': 'आउँदो परीक्षा',
  'up.latest': 'पछिल्लो परीक्षा',
  'up.view_all': 'सबै हेर्नुहोस्',
  'up.duration': '{n} मिनेट',
  'up.start_by':
    'पूरा {n} मिनेट पाउन {time} सम्ममा सुरु गर्नुहोस् — विन्डो बन्द हुँदा सबैको परीक्षा रोकिन्छ ।',
  'up.linear': 'एक पटकमा एउटा प्रश्न',
  'up.free_nav': 'प्रश्नहरू बीच स्वतन्त्र रूपमा जान सकिन्छ',
  'up.next': 'अर्को परीक्षा',

  'tips.title': 'यो हप्ताका सुझाव',
  'tips.also_practise': 'यीमा पनि अभ्यास गर्न सकिन्छ',
  'sub.manage': 'व्यवस्थापन',
  'sub.incl_vat': '{vat} भ्याट सहित',
  'sub.incl_registration': '{who} को एक पटकको दर्ता शुल्क {amount} समेत',
  'pending.register_title': 'दर्ता पूरा गर्नुहोस्',
  'pending.register_body':
    'एक पटकको दर्ता शुल्क नतिरेसम्म {who} ले परीक्षा दिन सक्दैन। यो शुल्क प्रति बच्चा '
    + 'एक पटक मात्र लाग्छ, फेरि कहिल्यै लाग्दैन।',
  'pending.register_cta': '{amount} तिर्नुहोस्',
  'pending.plan_title': 'दर्ता शुल्क तिरियो — अब योजना छान्नुहोस्',
  'pending.plan_body':
    'योजनाले साप्ताहिक परीक्षा, स्कोरकार्ड र रिपोर्ट खोल्छ। केही पनि आफैं काटिँदैन: '
    + 'तपाईं योजना छान्नुहोस् र तयार हुँदा भुक्तानी गर्नुहोस्।',
  'pending.plan_cta': 'योजना छान्नुहोस्',
  'parent.done': 'बुझाइयो',
  'parent.show_all_tests': 'सबै {n} परीक्षा हेर्नुहोस्',
  'parent.show_fewer_tests': 'थोरै देखाउनुहोस्',
  'parent.no_papers': 'यो कक्षाका लागि कुनै परीक्षा तय भएको छैन।',
  'parent.view_scorecard': 'नतिजा हेर्नुहोस्',
  'parent.awaiting_release': 'नतिजा आउन बाँकी',
  'parent.start_test': 'परीक्षा सुरु गर्नुहोस्',
  'parent.window_closed': 'समय सकियो',
  'parent.choose_child': 'बालबालिका छान्नुहोस्',
  'parent.add_child_to_see': 'परीक्षा हेर्न बालबालिका थप्नुहोस्।',
  'parent.subscription': 'सदस्यता',
  'parent.no_subscription': 'अहिलेसम्म कुनै सदस्यता छैन।',
  'plan.choose_title': 'योजना छान्नुहोस्',
  'plan.choose_sub':
    'साप्ताहिक परीक्षा सदस्यतामा चल्छ। दाजुभाइ-दिदीबहिनी एउटै योजनामा जान्छन्, र '
    + 'भुक्तानी गर्दा मासिक वा वार्षिक फेर्न सकिन्छ।',
  'plan.choose_note':
    'योजना छान्दा शुल्क लाग्दैन। खातामा थपिएपछि भुक्तानीका बटन देखिन्छन्।',
  'plan.no_children': 'पहिले बच्चा थप्नुहोस् — योजनाले खातामा भएका बच्चालाई समेट्छ।',
  'plan.who_for': 'यो योजना कसका लागि हो?',
  'plan.who_note':
    'एउटै योजनामा भएका बच्चाको बिल सँगै आउँछ। कसैलाई मासिक र कसैलाई वार्षिक '
    + 'राख्न पहिलो समूह अहिले छान्नुहोस्, बाँकी त्यसपछि।',
  'plan.pick_someone': 'कम्तीमा एक बच्चा छान्नुहोस्, अनि मिल्ने योजना देखिन्छ।',
  'plan.now_the_rest': 'भयो। तलबाट अरूका लागि योजना छान्नुहोस्।',

  // ------------------------------------------------------------- subjects
  'subjects.title': 'तपाईंका विषय',
  'subjects.sub': 'कुन विषय कस्तो छ र कतातिर गइरहेको छ।',
  'subjects.strongest': 'अहिलेसम्म सबैभन्दा बलियो: ',
  'subjects.most_room': 'सुधारको सबैभन्दा ठाउँ: ',
  'subjects.subject': 'विषय',
  'subjects.latest': 'पछिल्लो',
  'subjects.change': 'परिवर्तन',
  'subjects.tests': 'परीक्षा',

  // -------------------------------------------------------------- listen
  'listen.play': 'सुन्नुहोस्',
  'sc.spoken': '{paper} मा {name} ले {max} मध्ये {score} अंक ल्याउनुभयो। यो {pct} प्रतिशत हो।',
  'renew.spoken': '{children} का लागि {amount} तिर्न बाँकी छ, जुन {date} सम्मको हो।',
  'listen.stop': 'रोक्नुहोस्',

  // ----------------------------------------------------------- recognition
  'recog.title': 'यस हप्ताको सम्मान',
  'recog.sub': '{paper} · {total} मध्ये {n} जनाले {pct}% पुर्‍याए',
  'recog.sub_junior': '{paper} · {total} मध्ये {n} जनाले राम्रो गरे',
  'recog.you': 'तपाईं',
  'recog.private': 'यो Career Academy मा साइन इन गरेका परिवारले मात्र देख्न सक्छन्। सार्वजनिक वेबसाइटमा यो कहिल्यै देखिँदैन।',
  'recog.and_more': 'र थप {n} जना',

  // --------------------------------------------------------------- rewards
  'rewardcard.earn_title': 'एक महिना निःशुल्क कमाउनुहोस्',
  'rewardcard.monthly_title': 'मासिक पुरस्कार',
  'rewardcard.monthly_sub': 'पूरा महिनाभर 80% ल्याए अर्को महिना निःशुल्क।',
  'rewardcard.student': 'विद्यार्थी',
  'rewardcard.month': 'महिना',
  'rewardcard.score': 'अंक',

  // ------------------------------------------------------- billing handover
  'handover.title': 'उहाँलाई आफैं भुक्तानी गर्न दिनुहोस्',
  'handover.action': 'भुक्तानी जिम्मा दिनुहोस्',
  'handover.working': 'जिम्मा दिँदै…',

  // ---------------------------------------------------------- subscription
  'sub.verifying': 'गेटवेसँग जाँच हुँदै…',
  'sub.plan': 'योजना',
  'sub.state': 'अवस्था',
  'sub.covers': 'समेटिएका',
  'sub.renews': 'नवीकरण',
  'sub.amount': 'रकम',
  'sub.month_free': 'यो महिना निःशुल्क छ।',
  'sub.access_ok': 'परीक्षा दिन पाइन्छ।',
  'sub.access_locked': 'भुक्तानी पुष्टि नभएसम्म परीक्षा बन्द छ।',
  'sub.from': 'बाट',
  'sub.to': 'सम्म',
  'sub.reason': 'कारण',
  'sub.when': 'कहिले',

  // -------------------------------------------------------------- history
  'history.title': 'परीक्षा',
  'history.for_class': 'कक्षा {n}',
  'history.every_test': 'परीक्षाको इतिहास',
  'history.none_match': 'यी फिल्टरसँग मिल्ने परीक्षा छैन। थप हेर्न फिल्टर खुकुलो पार्नुहोस्।',
  'history.count': '{total} परीक्षा',
  'history.count_filtered': '{total} मध्ये {shown} परीक्षा',
  'history.not_submitted': 'बुझाइएको छैन',
  'history.ran_out': 'समय सकियो',
  'history.across_n': '{n} वटा प्रकाशित परीक्षाको आधारमा।',
  'history.all_time': 'फिल्टर गरिएका मात्र होइन, सबै प्रकाशित परीक्षा।',
  'history.right_out_of': '{attempted} मध्ये {correct} ठीक',
  'history.f_name': 'परीक्षाको नाम',
  'history.f_name_hint': 'खोज्नुहोस्…',
  'history.f_from': 'देखि',
  'history.f_to': 'सम्म',
  'history.f_status': 'नतिजा',
  'history.f_all': 'सबै',
  'history.f_out': 'नतिजा आएको',
  'history.f_waiting': 'नतिजा आउन बाँकी',
  'history.f_clear': 'फिल्टर हटाउनुहोस्',
  'combo.clear': 'यो फिल्ड खाली गर्नुहोस्',
  'combo.no_match': 'त्यो नामको परीक्षा छैन',
  'history.every_test_sub': 'पूरा कक्षाको नतिजा सार्वजनिक भएपछि अंक देखिन्छ।',
  'history.none': 'अहिलेसम्म कुनै परीक्षा दिइएको छैन।',
  'history.review': 'पुनरावलोकन',
  'history.what_to_work_on': 'केमा मेहनत गर्ने',
  'history.needs_practice': 'अभ्यास चाहिन्छ',
  'history.going_well': 'राम्रो चलिरहेको',
  'history.by_subject': 'विषयअनुसार',
  'history.right': 'ठीक',
  'history.wrong': 'गलत',
  'history.skipped': 'छाडिएको',
  'history.accuracy': 'शुद्धता',

  // -------------------------------------------------------------- support
  'support.with_team': 'हाम्रो टोलीसँग',
  'support.replied': 'जवाफ आयो — अब तपाईंको पालो',
  'support.resolved': 'समाधान भयो',
  'support.title': 'सहयोग',
  'support.sub': 'भुक्तानी, बिग्रिएको परीक्षा वा अरू जुनसुकै कुरा हामीलाई लेख्नुहोस्।',
  'support.not_about_child': 'कुनै खास बालबालिकाबारे होइन',
  'support.sending': 'पठाउँदै…',
  'support.conversation': 'कुराकानी',

  'support.closed': 'बन्द',
  'support.about_what': 'कुन विषयमा हो?',
  'support.subject_ph': 'जस्तै: साउनको बिल',
  'support.which_child': 'कुन बालबालिका?',
  'support.optional': '· ऐच्छिक',
  'support.your_message': 'तपाईंको सन्देश',
  'support.message_ph': 'के भयो लेख्नुहोस्। मिति र नाम भए थप्नुहोस्।',
  'support.send': 'पठाउनुहोस्',
  'support.all_conversations': '← सबै कुराकानी',
  'support.about_child': '{name} बारे',
  'history.subject': 'विषय',

  'support.you': 'तपाईं',
  'support.new_count': '{n} नयाँ',
  'support.started_by': '{name}ले सुरु गरेको',
  'support.closed_notice': 'यो कुराकानी बन्द भइसक्यो। नयाँ सुरु गर्नुहोस्, हामी हेर्नेछौं।',
  'support.reply': 'जवाफ',

  // ------------------------------------------------------------- scorecard
  'sc.title': 'नतिजा पत्र',
  'sc.student': 'विद्यार्थी',
  'sc.test': 'परीक्षा',
  'sc.note': 'टिप्पणी',
  'sc.correct': 'ठीक',
  'sc.wrong': 'गलत',
  'sc.unanswered': 'नदिइएको',
  'sc.auto_submitted': 'समय सकिँदा आफैँ बुझाइयो',
  'sc.by_subject': 'विषयअनुसार',
  'sc.subject': 'विषय',
  'sc.score': 'अंक',
  'sc.download_failed': 'डाउनलोड भएन',
  'sc.preparing': 'तयार हुँदै…',
  'sc.page_title': 'परीक्षा नतिजा',
  'sc.page_sub': 'तपाईंको अंक, र हरेक प्रश्न उत्तरसहित।',
  'sc.praise_high': 'उत्कृष्ट!',
  'sc.praise_good': 'राम्रो काम!',
  'sc.praise_ok': 'ठीकै छ।',
  'sc.praise_low': 'अभ्यास जारी राख्नुहोस्।',
  'sc.praise_body': 'तपाईंले {max} मध्ये {score} अंक प्राप्त गर्नुभयो।',
  'sc.of_max': '{max} मध्ये',
  'sc.subject_score': '{max} मध्ये {correct}',
  'sc.download_pdf': 'PDF डाउनलोड',

  // ---------------------------------------------------------- intelligence
  'intel.direction': 'सिकाइका जानकारी',
  'intel.direction_sub': 'समयअनुसारको अंक, सोही प्रश्नपत्र दिने समान विद्यार्थीको औसतसँगै।',
  'intel.class_average': 'कक्षाको औसत',
  'intel.early_reading': 'प्रारम्भिक अनुमान',
  'intel.chart_alt': 'समयअनुसारको अंक, सोही प्रश्नपत्र दिने समान विद्यार्थीको औसतसँग तुलना गरिएको।',
  'intel.showing_up': 'नियमितता र स्थिरता',
  'intel.compared': 'अरूसँग तुलना',
  'intel.what_next': 'अब के गर्ने',
  'intel.what_next_sub': 'सुझावले अभ्यास गर्ने सामग्री देखाउँछ — बालबालिकालाई अंक दिँदैन।',
  'intel.growth_index': 'वृद्धि सूचकांक',

  // ---------------------------------------------------------------- signup
  'signup.dev_mode':
    'विकास मोड — इमेल वा SMS सेवा सेट नभएकाले कुनै कोड पठाइएको छैन, र यो खाता प्रमाणित मानिएको छ। भुक्तानी नमुना ग्यालवेमा चल्छ।',
  'signup.dob': 'जन्म मिति',
  'signup.your_dob': 'तपाईंको जन्म मिति',
  'elective.legend': 'ऐच्छिक विषय',
  'elective.note':
    'कक्षा ८ देखि १० सम्म ३६ अंक अनिवार्य विषयबाट र १४ अंक तल छानिएको जोडीबाट आउँछ — प्रत्येक विषयबाट ७ अंक।',
  'elective.pair_marks': 'प्रत्येकको ७ अंक',
  'elective.additional_mathematics': 'अतिरिक्त गणित',
  'elective.computer_education': 'कम्प्युटर शिक्षा',
  'elective.office_practice_accountancy': 'कार्यालय अभ्यास तथा लेखा',
  'elective.business_studies': 'व्यवसाय अध्ययन',
  'elective.none': 'कुनै ऐच्छिक विषय तोकिएको छैन',
  'signup.title': 'दर्ता गर्नुहोस्',
  'signup.account_heading': 'आफ्नो खाता बनाउनुहोस्',
  'signup.account_lede': 'Career Academy मा सामेल हुनुहोस्, आजैबाट आफ्नो यात्रा सुरु गर्नुहोस्।',
  'signup.step_account': 'तपाईंको खाता',
  'signup.step_verify': 'मोबाइल पुष्टि',
  'signup.step_child': 'तपाईंको बालबालिका',
  'signup.step_register': 'दर्ता',
  'signup.step_plan': 'योजना छान्नुहोस्',
  'signup.step_pay': 'भुक्तानी',
  'signup.unconfirmed':
    'भुक्तानी अझै पुष्टि भएको छैन। खाताबाट रकम गएको भए केही मिनेटमै मिलान हुनेछ — '
    + 'यो बन्द गरेर ड्यासबोर्ड हेर्न सक्नुहुन्छ।',
  'signup.all_set': 'सबै तयार भयो',
  'signup.paid': 'भुक्तानी प्राप्त भयो।',
  'signup.paid_invoice': 'भुक्तानी प्राप्त भयो, बिल नं. {no}।',
  'signup.can_sit': '{who} अब आउँदो साप्ताहिक परीक्षा दिन सक्नुहुन्छ।',
  'signup.you': 'तपाईं',
  /* "एउटा काम बाँकी छ" (one thing left) dropped with the English: the
     registration ending shows this notice under its own remaining task, and
     two lines each calling themselves the last one contradict each other. */
  'signup.next_give_login':
    '{who} लाई आफ्नै लगइन दिनुहोस्। परीक्षा विद्यार्थीकै खाताबाट दिइन्छ, तपाईंको '
    + 'खाताबाट होइन। ड्यासबोर्ड खोलेर "तपाईंका बालबालिका" मा उहाँलाई खोज्नुहोस् र '
    + 'लगइन बनाउनुहोस्।',
  'signup.receipt_note':
    'रसिद इमेलमा पठाइयो। हरेक नवीकरण अघि SMS मार्फत सम्झना पठाइनेछ — '
    + 'खाताबाट आफैँ कहिल्यै रकम काटिँदैन।',
  'signup.go_dashboard': 'मेरो ड्यासबोर्डमा जानुहोस्',
  'signup.acct_self_sub': 'एउटै खाता: तपाईं आफैँ भुक्तानी गर्नुहुन्छ र आफैँ परीक्षा दिनुहुन्छ।',
  'signup.acct_parent_sub': 'खाता तपाईंको हो — बालबालिकाले पछि आफ्नै लगइन पाउँछन्।',
  'signup.i_am_parent': 'म अभिभावक हुँ',
  'signup.i_am_student': 'म विद्यार्थी हुँ, आफैँका लागि दर्ता गर्दै',
  'signup.i_am_student_short': 'म विद्यार्थी हुँ',
  'signup.role_parent_desc': 'म आफ्नो बालबालिकाको लागि खाता बनाउँदै छु',
  'signup.role_student_desc': 'आफैँका लागि दर्ता गर्दै',
  'signup.your_full_name': 'तपाईंको पूरा नाम',
  'signup.your_class': 'तपाईंको कक्षा',
  'signup.select': 'छान्नुहोस्',
  'signup.class_n': 'कक्षा {n}',
  'signup.your_stream': 'तपाईंको समूह',
  'signup.your_school': 'तपाईंको विद्यालय',
  'signup.guardian_title': 'तपाईंका अभिभावक वा संरक्षक',
  'signup.guardian_intro':
    'खाता उहाँकै नाममा रहन्छ: परीक्षाको शुल्क उहाँलाई जान्छ, सम्झनाहरू उहाँकै फोनमा आउँछन्, '
    + 'र तलको सहमति उहाँले नै दिनुहुन्छ। माथिको आफ्नो साइन-इन तपाईंकै रहन्छ।',
  'signup.guardian_name': 'अभिभावक वा संरक्षकको पूरा नाम',
  'signup.guardian_email': 'अभिभावक वा संरक्षकको इमेल',
  'signup.guardian_email_note': 'उहाँ यसैबाट साइन इन गर्नुहुन्छ, त्यसैले तपाईंकोभन्दा फरक हुनुपर्छ।',
  'signup.guardian_mobile': 'अभिभावक वा संरक्षकको मोबाइल',
  'signup.guardian_password': 'उहाँका लागि पासवर्ड',
  'signup.guardian_password_note':
    'अहिले एउटा राख्नुहोस् र उहाँलाई भन्नुहोस् — साइन इन गरेपछि उहाँले फेर्न सक्नुहुन्छ।',
  'signup.your_login': 'तपाईंको आफ्नै साइन-इन',
  'signup.enrolled_heading': 'हालसम्म दर्ता भएका',
  'signup.child_class_line': 'कक्षा {n}',
  'signup.child_login_heading': 'तपाईंको बच्चा कसरी साइन इन गर्छ',
  'signup.child_login_note':
    'परीक्षा तपाईंको होइन, बच्चाकै खाताबाट दिइन्छ। अहिले नै उहाँको साइन इन '
    + 'छान्नुहोस् र उहाँलाई भन्नुहोस् — पासवर्ड पछि फेर्न सकिन्छ।',
  'signup.child_login_email': 'उहाँको इमेल',
  'signup.child_login_password': 'उहाँका लागि पासवर्ड',
  'signup.signs_in_as': '{email} बाट साइन इन गर्नुहुन्छ',
  'signup.no_login_yet': 'अझै साइन इन छैन — परीक्षा दिन उहाँलाई आफ्नै चाहिन्छ',
  'signup.set_their_login': 'उहाँको साइन इन राख्नुहोस्',
  'signup.save_login': 'साइन इन सुरक्षित गर्नुहोस्',
  'signup.add_another': 'अर्को बच्चा थप्नुहोस्',
  'signup.continue_to_plan': 'योजना छान्न जानुहोस्',
  'signup.continue_to_registration': 'दर्तामा जानुहोस्',
  /* Re-translated with the step order: the button after this no longer goes to
     the plan, it goes to the joining fee. A stale translation here would send a
     Nepali reader to a step the English reader is not sent to. */
  'signup.next_child_hint':
    'अर्को बच्चाका लागि यो भर्नुहोस्, वा अगाडि बढ्नुहोस्। दर्ता शुल्क प्रति बच्चा एक पटक '
    + 'लाग्छ, र दाजुभाइ-दिदीबहिनी एउटै सदस्यतामा रहन्छन्।',
  'signup.plans_for_children': '{n} जना बच्चालाई समेट्ने योजनाहरू।',
  'signup.email': 'इमेल',
  'signup.mobile': 'मोबाइल',
  'ph.password_current': 'तपाईंको हालको पासवर्ड',
  'ph.password_signin': 'पासवर्ड लेख्नुहोस्',
  'ph.password_new': 'नयाँ पासवर्ड बनाउनुहोस्',
  'ph.password_confirm': 'फेरि लेख्नुहोस्',
  'ph.password_for_them': 'उहाँका लागि पासवर्ड बनाउनुहोस्',
  'ph.guardian_name': 'उहाँको पूरा नाम',
  'ph.guardian_email': 'उहाँको इमेल ठेगाना',
  'ph.school': 'विद्यालय वा क्याम्पसको नाम',
  'ph.section': 'A, B, C…',
  'ph.child_name': 'बच्चाको पूरा नाम',
  'ph.child_email': 'उहाँको आफ्नै इमेल ठेगाना',
  'ph.child_password': 'कम्तीमा १० अक्षर र अंक',
  'ph.your_name': 'तपाईंको पूरा नाम',
  'ph.code': '000000',
  'ph.invite_code': 'निमन्त्रणामा आएको कोड',
  'ph.message': 'के जान्न चाहनुहुन्छ लेख्नुहोस्',
  'signup.mobile_ph': '98XXXXXXXX',
  'signup.sms_note': 'परीक्षा र नवीकरणका सम्झना SMS मा आउँछन्, त्यसैले यो नम्बर चालु हुनुपर्छ।',
  'signup.password': 'पासवर्ड',
  'signup.address_legend': 'परिवार बस्ने ठेगाना',
  'signup.optional': 'ऐच्छिक',
  'signup.province': 'प्रदेश',
  'signup.province_ph': 'प्रदेश छान्नुहोस्',
  'signup.permanent_address': 'स्थायी ठेगाना',
  'signup.permanent_address_ph': 'वडा, नगरपालिका, जिल्ला',
  'signup.local_address': 'अस्थायी ठेगाना',
  'signup.local_address_ph': 'हाल बस्ने ठाउँ, फरक भए',
  'signup.password_hint': 'कम्तीमा १० अक्षर, अक्षर र अंक दुवै सहित।',
  'signup.ph_full_name': 'आफ्नो पूरा नाम लेख्नुहोस्',
  'signup.ph_email': 'आफ्नो इमेल ठेगाना लेख्नुहोस्',
  'signup.ph_password': 'पासवर्ड बनाउनुहोस्',
  'signup.continue_with_google': 'Google बाट जारी राख्नुहोस्',
  'auth.google_or': 'वा',
  'auth.google_expired':
    'त्यो साइन इनमा धेरै समय लाग्यो र म्याद सकियो। कृपया फेरि प्रयास गर्नुहोस्।',
  'auth.google_failed':
    'Google बाट साइन इन गर्न सकिएन। इमेल र पासवर्ड प्रयोग गर्नुहोस्, वा फेरि प्रयास गर्नुहोस्।',
  'auth.google_unavailable':
    'यो साइटमा Google साइन इन सेटअप गरिएको छैन। कृपया इमेल र पासवर्ड प्रयोग गर्नुहोस्।',
  'google.title': 'Google बाट साइन इन',
  'google.sub': 'पासवर्ड टाइप गर्नुको सट्टा आफ्नो Google खाता प्रयोग गर्नुहोस्।',
  'google.not_connected':
    'जोडिएको छैन। जोड्नुभयो भने अर्को पटक एकै ट्यापमा साइन इन गर्न सक्नुहुन्छ।',
  'google.connected_as': '{email} सँग जोडिएको।',
  'google.connect': 'Google जोड्नुहोस्',
  'google.leaving': 'Google मा लैजाँदै…',
  'google.disconnect': 'हटाउनुहोस्',
  'google.outcome_taken':
    'त्यो Google खाता अर्कै Career Academy खातासँग जोडिएको छ। त्यसमा साइन इन '
    + 'गर्नुहोस्, वा अर्को Google खाता प्रयोग गर्नुहोस्।',
  'google.outcome_expired': 'धेरै समय लाग्यो र म्याद सकियो। फेरि जोड्ने प्रयास गर्नुहोस्।',
  'google.outcome_failed': 'त्यति बेला Google सम्म पुग्न सकिएन। फेरि प्रयास गर्नुहोस्।',
  'google.outcome_unavailable': 'यो खातामा Google जोड्न मिल्दैन।',
  'google.needs_password_first':
    'यो खातामा जाने एउटै उपाय Google हो, त्यसैले हटाउँदा तपाईं बाहिर पर्नुहुन्छ। '
    + 'खातामा पासवर्ड पनि भएपछि हटाउन मिल्छ।',
  'google.disconnect_title': 'Google हटाउने?',
  'google.disconnect_body':
    'तपाईं आफ्नो इमेल र पासवर्डबाट साइन इन गर्नुहुनेछ। जहिले पनि फेरि जोड्न सक्नुहुन्छ।',
  'signup.google_or': 'वा तलको फारम भर्नुहोस्',
  'signup.google_signing_up_as':
    '{email} को रूपमा Google बाट दर्ता हुँदै। पासवर्ड चाहिँदैन — तपाईं उही Google '
    + 'खाताबाट साइन इन गर्नुहुनेछ।',
  'signup.agree_before': 'मैले',
  'signup.agree_after':
    'पढेँ र मेरो परिवारको यो जानकारी Career Academy ले राख्न म सहमत छु।',
  'signup.hide_it': 'लुकाउनुहोस्',
  'signup.read_it': 'पढ्नुहोस्',
  'signup.creating': 'बनाउँदै…',
  'signup.create_account': 'मेरो खाता बनाउनुहोस्',
  'signup.verify_title': 'मोबाइल पुष्टि गर्नुहोस्',
  'signup.verify_sub': 'हामीले SMS मा छ अंकको कोड पठायौँ।',
  'signup.dev_before': 'डेभलपमेन्ट मोड — तपाईंको कोड हो',
  'signup.dev_after': '। प्रोडक्सन बिल्डमा यो कहिल्यै देखिँदैन।',
  'signup.code': 'कोड',
  'signup.verify_btn': 'पुष्टि गर्नुहोस्',
  'signup.resend': 'फेरि पठाउनुहोस्',
  'signup.resend_in': '{s}s मा फेरि पठाउनुहोस्',
  'signup.send_code': 'कोड पठाउनुहोस्',
  'signup.mobile_verified': 'मोबाइल प्रमाणित भयो।',
  'signup.email_verified': 'इमेल प्रमाणित भयो।',
  'signup.email_note':
    'यो ठेगाना तपाईंकै हो भनी जाँच्न हामी यहाँ कोड पठाउँछौं। रसिद र नतिजा पनि यहीँ '
    + 'आउँछ, त्यसैले तपाईंले पढ्ने ठेगाना नै राख्नुहोस्।',
  'signup.code_not_sent':
    'अहिले कोड पठाउन सकिएन । केही बेरमा फेरि प्रयास गर्नुहोस्, नभए हामीलाई सम्पर्क गर्नुहोस् ।',
  'signup.code_wrong': 'कोड मिलेन। जाँच गरेर फेरि प्रयास गर्नुहोस्।',
  'signup.already_registered': 'यो इमेलबाट पहिले नै खाता छ।',
  'signup.mobile_registered':
    'यो मोबाइल नम्बर पहिले नै कुनै खातामा दर्ता छ। आफूले प्रयोग गरेको इमेलबाट साइन इन '
    + 'गर्नुहोस्, वा अर्को नम्बर राख्नुहोस्।',
  'signup.both_registered': 'यो इमेल र यो मोबाइल नम्बर दुवै पहिले नै खातामा दर्ता छन्।',
  'signup.sign_in_instead': 'साइन इन गर्नुहोस्',
  'signup.verify_note':
    'यो चरण अनिवार्य छ: नेपालमा नवीकरण सम्झना तपाईंसम्म पुग्नुमै भर पर्छ, त्यसैले '
    + 'रकम लिनुअघि नम्बर पुष्टि गर्छौं।',
  'signup.child_sub': 'पछि ड्यासबोर्डबाट थप बालबालिका थप्न सकिन्छ।',
  'signup.full_name': 'पूरा नाम',
  'signup.class': 'कक्षा',
  'signup.section': 'सेक्सन',
  'signup.school': 'विद्यालय',
  'signup.continue': 'अगाडि बढ्नुहोस्',
  'signup.plans_for_class': 'कक्षा {n} का लागि उपलब्ध योजनाहरू।',
  'signup.finish_payment': 'भुक्तानी पूरा गर्नुहोस्',
  'signup.pay_waiting':
    'भुक्तानी बाँकी रहेको सदस्यता छ। ड्यासबोर्ड खोलेर भुक्तानी गर्नुहोस् — '
    + 'रकम र योजना त्यहीँ देखिन्छ।',
  'signup.plan': 'योजना',
  'signup.for': 'कसका लागि',
  'signup.for_value': '{name} (कक्षा {n})',
  'signup.first_cycle': 'पहिलो भुक्तानी',
  'signup.registration': 'दर्ता शुल्क',
  'signup.registration_once': 'प्रति बालबालिका एक पटक',
  'promo.public_one':
    'परिचयात्मक अफर: तपाईंको पहिलो परीक्षा निःशुल्क। सुरु गर्न NPR 100 दर्ता शुल्क '
    + 'मात्र तिर्नुहोस् — योजनाको शुल्क त्यसपछि सुरु हुन्छ।',
  'promo.public_other':
    'परिचयात्मक अफर: तपाईंका पहिलो {n} परीक्षा निःशुल्क। सुरु गर्न NPR 100 दर्ता शुल्क '
    + 'मात्र तिर्नुहोस् — योजनाको शुल्क त्यसपछि सुरु हुन्छ।',

  'signup.promo_free_tests_one': 'तपाईंको पहिलो परीक्षा निःशुल्क छ।',
  'signup.promo_free_tests_other': 'तपाईंका पहिलो {n} परीक्षा निःशुल्क छन्।',
  'signup.promo_then_one':
    '{plan} — निःशुल्क परीक्षा प्रयोग भएपछि {amount} सुरु हुन्छ।',
  'signup.promo_then_other':
    '{plan} — {n} निःशुल्क परीक्षा प्रयोग भएपछि {amount} सुरु हुन्छ।',
  'signup.promo_remaining_one': '१ निःशुल्क परीक्षा बाँकी',
  'signup.promo_remaining_other': '{n} निःशुल्क परीक्षा बाँकी',
  'signup.promo_used_up': 'तपाईंका निःशुल्क परीक्षाहरू प्रयोग भइसके।',
  'fb.title': 'यो परीक्षा कस्तो लाग्यो?',
  'fb.sub': 'केही छोटा प्रश्न। चाहनुहुन्न भने छोड्न सक्नुहुन्छ।',
  'fb.send': 'पठाउनुहोस्',
  'fb.sending': 'पठाउँदै…',
  'fb.skip': 'छोड्नुहोस्',
  'fb.thanks': 'धन्यवाद — यसले हामीलाई राम्रो प्रश्नपत्र बनाउन मद्दत गर्छ।',
  'fb.failed': 'पठाउन सकिएन। तपाईंको अंक भने सुरक्षित छ।',
  'signup.amount': 'रकम',
  'signup.vat_included': 'भ्याट सहित',
  'signup.pay_esewa': 'eSewa बाट तिर्नुहोस्',
  'signup.pay_khalti': 'Khalti बाट तिर्नुहोस्',
  'signup.pay_fonepay': 'स्क्यान गरेर तिर्नुहोस् (कुनै पनि बैंक)',
  'qr.scan_with_any':
    '{amount} तिर्न कुनै पनि बैंक वा वालेट एपबाट यो स्क्यान गर्नुहोस्। '
    + '{gateway} सञ्जालमा भएका मोबाइल बैंकिङ, eSewa, Khalti सबैबाट चल्छ।',
  'qr.alt': 'बैंकिङ एपबाट स्क्यान गर्ने QR कोड',
  'qr.note':
    'यो पृष्ठ खुला राख्नुहोस्। भुक्तानी आउनेबित्तिकै आफैँ अपडेट हुन्छ — तपाईंले '
    + 'फर्केर भन्नु पर्दैन।',
  'qr.waiting': 'भुक्तानीको प्रतीक्षामा…',
  'qr.timed_out':
    'अझै भुक्तानी भएको छैन। पठाइसक्नुभएको छ भने केही मिनेटमै देखिन्छ — केही हराउँदैन।',
  'qr.cannot_check':
    'अहिले सर्भरसँग जाँच्न सकिएन। भुक्तानी गरिसक्नुभएको छ भने पनि समातिन्छ।',
  'qr.image_failed': 'QR बनाउन सकिएन। कृपया अर्को भुक्तानी विधि प्रयोग गर्नुहोस्।',
  'qr.paid': 'भुक्तानी भयो — धन्यवाद।',
  'qr.paid_invoice': 'भुक्तानी भयो — धन्यवाद। बिल {no}।',
  'signup.pay_note':
    'स्वतः पुनः शुल्क लिइँदैन। हरेक नवीकरण अघि सम्झना पठाउँछौं र निर्णय तपाईंको हुन्छ।',
  'signup.pay_unavailable':
    'अनलाइन भुक्तानी अहिले उपलब्ध छैन। भुक्तानीका लागि कार्यालयमा सम्पर्क गर्नुहोस् — '
    + 'तपाईंले भर्नुभएको सबै विवरण सुरक्षित छ, र भुक्तानी प्राप्त भएपछि हामी सदस्यता '
    + 'सक्रिय गरिदिनेछौं।',

  'common.skip_to_content': 'मुख्य सामग्रीमा जानुहोस्',

  'auth.tagline': 'साप्ताहिक मूल्याङ्कन प्लेटफर्म',

  // ------------------------------------------------------------ renew link
  'renew.title': 'सदस्यता नवीकरण गर्नुहोस्',
  'renew.covered_until': 'यो मितिसम्म चालु',
  'renew.no_password': 'पासवर्ड चाहिँदैन — यो लिंकले यही सदस्यताको भुक्तानी मात्र गर्न सक्छ।',
  'renew.starting': '{gateway} मा लैजाँदै…',
  'renew.invalid': 'यो लिंकको म्याद सकियो वा मान्य छैन।',
  'renew.sign_in_instead': 'खाताबाट नवीकरण गर्न साइन इन गर्नुहोस्',
  'renew.see_everything': 'नतिजा र अरू हेर्न साइन इन गर्नुहोस्',
  'renew.pending_retry': 'फेरि जाँच्नुहोस्',
  'renew.reason_cancelled': 'यो सदस्यता रद्द भइसक्यो। नयाँ सुरु गर्न साइन इन गर्नुहोस्।',
  'renew.reason_mobile_unverified':
    'पहिले मोबाइल नम्बर पुष्टि गर्नुहोस् — साइन इन गरेर पुष्टि गर्नुहोस्, अनि खाताबाट नवीकरण गर्नुहोस्।',
  'renew.reason_covered_by_reward': 'यो चक्र मासिक पुरस्कारले समेटेको छ, तिर्नुपर्ने केही छैन।',
  'renew.reason_already_renewed': 'नवीकरण भइसक्यो — {date} सम्म चालु छ।',

  // --------------------------------------------------------------- billing
  'billing.title': 'भुक्तानी',
  'billing.due_by': 'यो मितिभित्र',
  'billing.covered_until': 'यो मितिसम्म चालु',
  'billing.test_access': 'परीक्षा पहुँच',
  'billing.access_open': 'खुला',
  'billing.access_locked': 'बन्द',
  'billing.pay_below': 'तलको सदस्यता कार्डबाट Khalti मार्फत तिर्नुहोस्।',
  'billing.no_subscription': 'अहिलेसम्म कुनै सदस्यता छैन।',
  'billing.pay_early': 'चाँडै भुक्तानी वा वार्षिकमा बदल्नुहोस्',
  'billing.one_cycle_only':
    'यी फरक-फरक बिलिङ चक्रमा छन्, त्यसैले अलग-अलग भुक्तानी हुन्छ। पहिले मासिक '
    + 'वा वार्षिक छान्नुहोस्, अनि बाँकी।',
  'billing.tab_due': 'अहिले तिर्नुपर्ने',
  'billing.tab_plans': 'सदस्यता',
  'billing.tab_invoices': 'बिलहरू',
  'billing.tab_activity': 'गतिविधि',
  'billing.activity': 'खाताको गतिविधि',
  'billing.activity_sub': 'सदस्यतामा भएका हरेक परिवर्तन, नयाँ पहिले — के तिरियो, के सकियो, के पुनः सुरु भयो।',
  'billing.no_activity': 'तपाईंको सदस्यतामा अहिलेसम्म केही परिवर्तन भएको छैन।',
  'billing.history': 'तपाईंले तिर्नुभएको',
  'billing.no_history': 'अहिलेसम्म कुनै भुक्तानी छैन।',
  'billing.invoice': 'बिल नं.',
  'billing.when': 'कहिले',
  'billing.what_for': 'केका लागि',
  'billing.paid_with': 'कसरी तिरेको',
  'billing.amount': 'रकम',
  'billing.at_the_office': 'कार्यालयमा',
  'billing.show_all': 'सबै {n} देखाउनुहोस्',
  'billing.show_recent': 'पछिल्ला मात्र देखाउनुहोस्',

  'billing.page_sub': 'तपाईंले तिर्नुपर्ने, कसका लागि, र अहिलेसम्म तिर्नुभएको सबै।',
  'billing.due_title': 'अहिले तिर्नुपर्ने',
  'billing.due_sub': 'कसका लागि तिर्ने छान्नुहोस्। तिर्नुपर्ने सबै छानिएका छन्; यसपटक नतिर्ने भए हटाउनुहोस्।',
  'billing.nothing_owed': 'अहिले तिर्नुपर्ने केही छैन।',
  'billing.no_children': 'यो सदस्यतामा कुनै बालबालिका छैनन्',
  'billing.covers_to': '{date} सम्म चालु',
  'promo.registered_one':
    'परिचयात्मक अफर: तपाईंको पहिलो परीक्षा निःशुल्क। दर्ता शुल्क तिरिसकिएको छ, त्यसैले '
    + 'योजनाको शुल्क त्यसपछि मात्र सुरु हुन्छ।',
  'promo.registered_other':
    'परिचयात्मक अफर: तपाईंका पहिलो {n} परीक्षा निःशुल्क। दर्ता शुल्क तिरिसकिएको छ, '
    + 'त्यसैले योजनाको शुल्क ती परीक्षापछि मात्र सुरु हुन्छ।',
  'billing.includes_registration': '{names} को दर्ता शुल्क {amount} समेत, एक पटक मात्र',
  'billing.selected_total': '{n} छानिएको',
  'billing.redirecting': '{gateway} मा लैजाँदै…',
  'billing.paid': 'भुक्तानी प्राप्त भयो, बिल नं. {no}।',
  'billing.verify_mobile_first': 'तिर्नुअघि मोबाइल नम्बर पुष्टि गर्नुहोस् — नवीकरणका सम्झना SMS मा आउँछन्।',
  'billing.subscriptions': 'तपाईंका सदस्यता',
  'billing.subscriptions_sub': 'प्रति कक्षा समूह एउटा, किनकि योजनाको मूल्य समूहअनुसार हुन्छ।',
  'billing.child': 'बालबालिका',
  'billing.next_amount': 'अर्को भुक्तानी',
  'billing.free': 'निःशुल्क',
  'billing.history_sub': 'यो खाताका सबै बिल।',

  'billing.how_often': 'कति-कति समयमा तिर्न चाहनुहुन्छ?',
  'billing.cycle_monthly': 'मासिक',
  'billing.cycle_quarterly': 'हरेक तीन महिना',
  'billing.cycle_annual': 'वार्षिक',
  'billing.save_amount': '{amount} बचत',
  'billing.per_month': 'महिनाको {amount}',

  // ------------------------------------------------------- performance line
  'perf.up': '{name}को प्रगति भइरहेको छ',
  'perf.up_self': 'तपाईंको प्रगति भइरहेको छ',
  'perf.down': '{name}को अंक घट्दै छ',
  'perf.down_self': 'तपाईंको अंक घट्दै छ',
  'perf.steady': '{name} स्थिर हुनुहुन्छ',
  'perf.steady_self': 'तपाईं स्थिर हुनुहुन्छ',
  'perf.early': 'दिशा भन्न अझै चाँडो भयो — केही शनिबारपछि देखिनेछ',
  'perf.provisional': 'हालसम्म',
  'perf.weakest': '{subject} मा कमजोर ({asked} मध्ये {correct})',
  'perf.next': '{advice}',

  // -------------------------------------------------------------- security
  'security.devices': 'साइन इन भएका उपकरण',
  'security.devices_sub': 'तपाईंको खातामा अहिले पुग्न सक्ने सबै ब्राउजर।',
  'security.device': 'उपकरण',
  'security.signed_in': 'साइन इन भएको',
  'security.none_other': 'अरू कुनै सक्रिय सत्र छैन।',
  'security.signs_out_all': 'यसले यही उपकरण सहित सबैबाट साइन आउट गर्छ।',
  'security.device_on': '{os} मा {browser}',
  'security.unknown_app': 'नचिनिएको एप',
  'security.unknown_system': 'अज्ञात प्रणाली',
  'security.a_browser': 'ब्राउजर',

  // -------------------------------------------------------------- password
  'password.title': 'पासवर्ड',
  'password.changed': 'पासवर्ड परिवर्तन भयो।',
  'password.sign_in_again': 'फेरि साइन इन गर्नुहोस्',
  'password.signs_out_sub': 'परिवर्तन गर्दा यही सहित सबै उपकरणबाट साइन आउट हुन्छ।',
  'password.too_short': 'कम्तीमा १० अक्षर प्रयोग गर्नुहोस्।',
  'password.needs_both': 'अक्षर र अंक दुवै राख्नुहोस्।',

  // ---------------------------------------------------------- answer review
  'review.answers': 'उत्तरहरू',
  'review.every_question': 'सबै प्रश्न',
  'review.right': 'ठीक',
  'review.wrong': 'गलत',
  'review.not_answered': 'उत्तर दिइएन',
  'review.title': 'प्रश्न समीक्षा',
  'review.sub': 'हरेक प्रश्न हेर्नुहोस् — सही उत्तर र तपाईंको उत्तर।',
  'review.filter_all': 'सबै प्रश्न',
  'review.filter_wrong': 'गलत मात्र',
  'review.filter_unanswered': 'उत्तर नदिएका मात्र',
  'review.none_match': 'त्यो फिल्टरमा देखाउन केही छैन।',
  'review.your_answer': 'तपाईंको उत्तर',
  'review.expand': 'विकल्पहरू देखाउनुहोस्',
  'review.collapse': 'विकल्पहरू लुकाउनुहोस्',
  'review.answered': 'उत्तर दिइयो',

  // -------------------------------------------------------- student screens
  'insights.what_next': 'अब के गर्ने',
  'insights.what_practise': 'के अभ्यास गर्ने',
  'insights.what_practise_sub':
    'तपाईंले दिनुभएका सबै प्रश्नपत्र हेरेर — पछिल्लो मात्र होइन। त्यसैले यहाँ '
    + 'पछिल्लो नतिजाभन्दा फरक विषय देखिन सक्छ।',
  'insights.how_doing': 'तपाईं कस्तो गर्दै हुनुहुन्छ',
  'insights.how_doing_sub': 'समयअनुसार तपाईंको अंक, सोही प्रश्नपत्र दिने विद्यार्थीको औसतसँगै।',
  'insights.turning_up': 'नियमितता र स्थिरता',
  'student.no_student': 'कुनै विद्यार्थी छानिएको छैन',
  'student.not_linked': 'यो खाता कुनै विद्यार्थी प्रोफाइलसँग जोडिएको छैन।',
  'student.after_that': 'त्यसपछि',
  'student.after_that_sub': 'तालिकामा रहेका आउँदा शनिबारहरू।',
  'student.your_next_test': 'तपाईंको आउँदो परीक्षा',

  // ------------------------------------------------------------ my account
  'account.my_profile': 'मेरो प्रोफाइल',
  'account.how_we_contact': 'हामी कसरी सम्पर्क गर्छौं',
  'account.mobile_ph': '9801234567',
  'account.code_ph': '000000',

  // ------------------------------------------------------- subscription card
  'sub.redirecting': '{gateway} मा लैजाँदै…',
  'sub.pay_verified': 'भुक्तानी पुष्टि भयो। बिल नं. {no} जारी भयो।',
  'sub.pay_unavailable':
    'अनलाइन भुक्तानी अहिले उपलब्ध छैन। भुक्तानीका लागि कार्यालयमा सम्पर्क गर्नुहोस्, '
    + 'हामी तपाईंको सदस्यता अद्यावधिक गरिदिनेछौं।',
  'app.pay_on_web': "वेबसाइटमा भुक्तानी गर्नुहोस्",
  'app.pay_on_web_note': "भुक्तानी तपाईंको फोनको ब्राउजरमा खुल्छ, जहाँ तपाईं पहिले नै साइन इन हुनुहुन्छ। भुक्तानी भएपछि एपमा फर्कनुहोस्।",
  'app.pay_unavailable':
    'Android एपमा भुक्तानी लिइँदैन।',
  'sub.see_activity': 'यो सदस्यताका सबै परिवर्तन हेर्नुहोस्',

  // ---------------------------------------------------------- the analysis
  'an.up': 'सुरुदेखि {points} अंकले माथि — पछिल्ला प्रश्नपत्र सुरुकाभन्दा राम्रा छन्।',
  'an.down': 'सुरुदेखि {points} अंकले तल। कुन विषयमा घट्यो हेर्नु राम्रो — सबैमा घट्यो भन्ने नठान्नुहोस्।',
  'an.steady': 'स्थिर छ। अंक अलिअलि तलमाथि हुन्छन्, तर अझै कुनै स्पष्ट दिशा छैन।',
  'an.early_reading': 'तीन प्रश्नपत्रबाट भनिएको हो, त्यसैले यसलाई पक्का नभई सुरुवाती ठान्नुहोस्।',
  'an.spread': 'पछिल्ला {papers} प्रश्नपत्रमा अंक एकआपसमा {points} अंकभित्रै रहे।',
  'an.spread_early': 'हप्ता-हप्ताको स्थिरता भन्न चार प्रश्नपत्र चाहिन्छ — हालसम्म {papers} छन्।',
  'an.no_comparison': 'अरू विद्यार्थीसँगको तुलना अझै देखाइएको छैन। कक्षाका थप विद्यार्थीले परीक्षा दिएपछि देखिनेछ।',
  'an.r0_one': 'हालसम्म एउटा नतिजा — आउँदो शनिबारपछि दिशा भन्न पुग्ने हुन्छ।',
  'an.r0_many': 'हालसम्म {papers} नतिजा — आउँदो शनिबारपछि दिशा भन्न पुग्ने हुन्छ।',
  'an.r1': 'यो हप्ता {subject} अभ्यास गर्नुहोस् — सबै प्रश्नपत्रमा {attempted} मध्ये {correct} ठीक।',
  'an.r2': '{subject} फेरि हेर्नुहोस् — पछिल्ला प्रश्नपत्रमा त्यहाँ घट्यो।',
  'an.r3': 'नियमितता कायम राख्नुहोस्; थप चुनौतीका लागि {subject} मा कठिन अभ्यास गर्नुहोस्।',
  'an.r4': 'हरेक शनिबार परीक्षा दिने लक्ष्य राख्नुहोस् — दिइएका प्रश्नपत्रबाट मात्र दिशा थाहा हुन्छ।',
  'an.r5': 'अंक हप्ता-हप्ता तलमाथि हुन्छन्; छोटो तर नियमित अभ्यासले स्थिर बनाउँछ।',
  'an.r5_junior': 'नतिजा हप्ता-हप्ता फरक पर्छ; छोटो र नियमित अभ्यासले स्थिर बनाउँछ।',
  'an.r6': '{subject} मा समय तोकेर अभ्यास गर्नुहोस् — धेरै प्रश्न छाडिएका थिए।',
  'an.r7': 'साप्ताहिक लय कायम राख्नुहोस् — यो हप्ता विशेष ध्यान दिनुपर्ने केही छैन।',

  'an.no_results': 'अझै कुनै नतिजा छैन — पहिलो शनिबारबाट रेखा सुरु हुनेछ।',
  'an.no_direction': 'दिशा भन्न अझै चाँडो भयो। केही थप शनिबारपछि देखिनेछ।',
  'an.series_self': 'तपाईंको अंक',
  'an.series_other': 'उहाँको अंक',
  'an.practise_junior': 'कक्षा १–३ को अभ्यास चार्टबाट होइन, शिक्षकको टिपोटबाट आउँछ।',
  'an.practise_none':
    'विशेष काम चाहिन्छ भन्न कुनै पनि विषयमा पर्याप्त आधार छैन — साप्ताहिक लय कायम राख्नुहोस्।',
  'an.no_tests': 'अझै कुनै परीक्षा दिइएको छैन।',
  'an.sat_of': '{offered} मध्ये {sat} शनिबार परीक्षा दिनुभयो।',
  'an.not_covered_one': '1 शनिबार सदस्यताले समेटेको थिएन।',
  'an.not_covered_many': '{count} शनिबार सदस्यताले समेटेका थिएनन्।',
  'an.needs_work_row': 'सबै प्रश्नपत्रमा {attempted} मध्ये {correct} ठीक',
  'an.needs_work_row_self': 'सबै प्रश्नपत्रमा {attempted} मध्ये {correct} ठीक',

  // Nepali closes a sentence with a danda, not a full stop.
  'perf.stop': '।',

  // ------------------------------------------------------------ exam runner
  'exam.question_n_of': 'प्रश्न {n} / {total}',
  'exam.resumed': 'पुनः सुरु — तपाईंका उत्तर र बाँकी समय सर्भरबाट फिर्ता आयो',
  'exam.linear_hint': 'एक पटकमा एउटा प्रश्न। अघि बढेपछि पछाडि फर्कन मिल्दैन।',
  'exam.autosave_hint': 'उत्तर आफैं सुरक्षित हुँदै जान्छ',
  'exam.q_marks_one': 'प्रश्न {n} · 1 अंक',
  'exam.q_marks_other': 'प्रश्न {n} · {marks} अंक',
  'exam.previous': 'अघिल्लो',
  'exam.next': 'अर्को',
  'exam.handoff_title': 'तयार हुनुभएपछि',
  'exam.handoff_body': 'पहिले बालबालिकासँगै बस्नुहोस्। तपाईंले “सुरु गर्नुहोस्” थिच्नुभएपछि मात्र समय गणना सुरु हुन्छ।',
  'exam.handoff_length': 'यो परीक्षा लगभग {minutes} मिनेटको हुन्छ।',
  'exam.handoff_begin': 'परीक्षा सुरु गर्नुहोस्',
  'exam.handoff_not_yet': 'अहिले होइन',
  'exam.mark': 'पुनरावलोकनका लागि चिन्ह',
  'exam.unmark': 'चिन्ह हटाउनुहोस्',
  'exam.submit': 'परीक्षा बुझाउनुहोस्',
  'exam.submitting': 'बुझाउँदै…',
  'exam.choose_to_continue': 'अघि बढ्न उत्तर छान्नुहोस्। यो प्रश्नमा फेरि फर्कन मिल्दैन।',
  'exam.confirm_title': 'परीक्षा बुझाउने हो?',
  'exam.confirm_all_done':
    'तपाईंले सबै प्रश्नको उत्तर दिनुभयो। बुझाएपछि उत्तर परिवर्तन गर्न मिल्दैन।',
  'exam.confirm_unanswered_one':
    '1 प्रश्नको उत्तर बाँकी छ। बुझाएपछि त्यसमा फर्कन मिल्दैन।',
  'exam.confirm_unanswered_other':
    '{n} प्रश्नको उत्तर बाँकी छ। बुझाएपछि तिनमा फर्कन मिल्दैन।',
  'exam.not_sent': 'तपाईंको परीक्षा अझै पठाइएको छैन।',
  'exam.back_to_dashboard': 'ड्यासबोर्डमा फर्कनुहोस्',

  'sit.student_signs_in': '{name} ले आफ्नै साइन इनबाट यो परीक्षा दिनुहुन्छ।',
  'sit.needs_login': '{name} को अझै साइन इन छैन। परीक्षा दिन उहाँलाई साइन इन चाहिन्छ।',
  'sit.give_login': 'उहाँलाई साइन इन दिनुहोस्',
  'sit.not_yours': 'यो परीक्षा तपाईंले दिने होइन।',
  'sit.parent_blocked_title': 'यो परीक्षा विद्यार्थी आफैंले दिन्छन्',

  // परीक्षा छोडेर जाँदा। कसैलाई दोष दिने होइन — बाहिर बिताएको समय टिपिन्छ,
  // परीक्षा रद्द हुँदैन।
  'exam.away_first':
    'तपाईं परीक्षाबाट बाहिर जानुभयो। बाहिर बिताएको समय तपाईंको उत्तरपुस्तिकामा टिपिएको छ। '
    + 'परीक्षा सकिन्जेल यही पृष्ठमा रहनुहोस्।',
  'exam.away_again':
    'तपाईं परीक्षाबाट {n} पटक बाहिर जानुभयो। बाहिर बिताएको समय तपाईंको उत्तरपुस्तिकामा टिपिएको छ। '
    + 'परीक्षा सकिन्जेल यही पृष्ठमा रहनुहोस्।',
  'exam.away_dismiss': 'परीक्षा जारी राख्नुहोस्',
  'exam.fs_enter': 'स्क्रिनमा अरू केही नदेखियोस् भन्नका लागि यो परीक्षा पूरा स्क्रिनमा दिनुहोस्।',
  'exam.fs_go': 'पूरा स्क्रिनमा जानुहोस्',
  'exam.fs_left': 'तपाईं पूरा स्क्रिनबाट बाहिर निस्कनुभयो। यो तपाईंको उत्तरपुस्तिकामा टिपिएको छ।',
  'exam.fs_back': 'पूरा स्क्रिनमा फर्कनुहोस्',
  'exam.fs_dismiss': 'अहिले होइन',

  'save.all_saved': 'सबै उत्तर सुरक्षित छन्।',
  'save.saving_one': 'तपाईंको उत्तर सुरक्षित गर्दै…',
  'save.saving_other': '{n} उत्तर सुरक्षित गर्दै…',
  'save.failed_one':
    'तपाईंको उत्तर सुरक्षित हुन सकेको छैन। यो यहीँ उपकरणमा राखिएको छ र '
    + 'जोडिनासाथ पठाइनेछ।',
  'save.failed_other':
    '{n} उत्तर सुरक्षित हुन सकेका छैनन्। ती यहीँ उपकरणमा राखिएका छन् र '
    + 'जोडिनासाथ पठाइनेछन्।',
  'save.offline_free_none': 'तपाईं अफलाइन हुनुहुन्छ। उत्तर दिइरहनुहोस् — केही हराउँदैन।',
  'save.offline_free_one':
    'तपाईं अफलाइन हुनुहुन्छ। तपाईंको उत्तर यहीँ उपकरणमा राखिएको छ र '
    + 'इन्टरनेट फर्किएपछि पठाइनेछ।',
  'save.offline_free_other':
    'तपाईं अफलाइन हुनुहुन्छ। {n} उत्तर यहीँ उपकरणमा राखिएका छन् र '
    + 'इन्टरनेट फर्किएपछि पठाइनेछन्।',
  'save.offline_linear_none':
    'तपाईं अफलाइन हुनुहुन्छ। यो परीक्षा एक पटकमा एउटा प्रश्न अघि बढ्ने भएकाले '
    + 'इन्टरनेट नफर्किई अघि बढ्न सकिँदैन। तपाईंले गरेको केही हराएको छैन।',
  'save.offline_linear_one':
    'तपाईं अफलाइन हुनुहुन्छ। यो परीक्षा एक पटकमा एउटा प्रश्न अघि बढ्ने भएकाले '
    + 'इन्टरनेट नफर्किई अघि बढ्न सकिँदैन। तपाईंको उत्तर यहीँ सुरक्षित छ।',
  'save.offline_linear_other':
    'तपाईं अफलाइन हुनुहुन्छ। यो परीक्षा एक पटकमा एउटा प्रश्न अघि बढ्ने भएकाले '
    + 'इन्टरनेट नफर्किई अघि बढ्न सकिँदैन। {n} उत्तर यहीँ सुरक्षित छन्।',

  // ----------------------------------------------------------------- reward
  'reward.headline': 'यस महिना 80% वा बढी ल्याए अर्को महिना निःशुल्क।',
  'reward.body':
    'एउटा राम्रो परीक्षाले मात्र होइन, महिनाभरका हरेक शनिबार गनिन्छ — र छुटेको '
    + 'परीक्षा शून्य गनिन्छ। अंक जत्तिकै उपस्थिति पनि महत्त्वपूर्ण छ।',

  // ------------------------------------------------------------------- tour
  'tour.title': 'यो पृष्ठ चिनाऔं',
  'tour.step_of': 'चरण {n} / {total}',
  'tour.back': 'पछाडि',
  'tour.next': 'अर्को',
  'tour.done': 'भयो',
  'tour.skip': 'छाड्नुहोस्',
  'tour.replay': 'फेरि चिनाइदिनुहोस्',
  'tour.replay_sub': 'यो ड्यासबोर्डको छोटो परिचय, फेरि।',
  'tour.replay_cta': 'परिचय सुरु गर्नुहोस्',

  'tour.step_pending_title': 'अझै बाँकी के छ',
  'tour.step_pending_body':
    'तपाईंको बच्चा र शनिबारको बीचमा जे बाँकी छ, त्यो यहीँ देखिन्छ — पहिले भर्ना '
    + 'शुल्क, त्यसपछि योजना। यो कार्ड नदेखिएपछि उनी तयार हुन्छन्।',

  'tour.step_children_title': 'हरेक बच्चालाई आफ्नै लगइन चाहिन्छ',
  'tour.step_children_body':
    'परीक्षा विद्यार्थीको आफ्नै खाताबाट दिइन्छ, तपाईंको खाताबाट होइन। "तपाईंका '
    + 'बच्चाहरू" मा गएर "लगइन दिनुहोस्" छान्नुहोस् — लगइन नभई शनिबारको परीक्षा दिन '
    + 'मिल्दैन।',

  'tour.step_papers_title': 'शनिबारको परीक्षा',
  'tour.step_papers_body':
    'हरेक शनिबार परीक्षा तोकिएको समयमा खुल्छ र आफैं बन्द हुन्छ। त्यो समयभित्र नदिइएको '
    + 'परीक्षा शून्य गनिन्छ, त्यसैले SMS सम्झना पढ्नु राम्रो।',

  'tour.step_scorecard_title': 'त्यसपछि के आउँछ',
  'tour.step_scorecard_body':
    'शनिबारको परीक्षा जाँचिएपछि यो ड्यासबोर्ड भरिन्छ — स्कोरकार्ड, विषयअनुसारको '
    + 'विवरण र परीक्षाहरूको प्रगति। आज खाली हुनुको अर्थ पहिलो शनिबार आइसकेको छैन '
    + 'भन्ने मात्र हो।',
};
